import { Transition, Dialog } from '@headlessui/react'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { IoMdClose } from 'react-icons/io'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../app/store'
import { closeOtpModal, setOtpLoading } from '../../../features/withdraw-otp/withdrawOtpSlice'

let currentWithdrawOTPIndex = 0

interface WithdrawOtpModalProps {
  onOtpSubmit: (challengeId: string, otpCode: string) => Promise<void>
}

export const WithdrawOtpModal: React.FC<WithdrawOtpModalProps> = ({ onOtpSubmit }) => {
  const dispatch = useDispatch()
  
  const { isOpen, challengeId, ttlSeconds, isLoading } = useSelector(
    (state: RootState) => state.withdrawOtp
  )

  // OTP state
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(''))
  const [activeOtpIndex, setActiveOtpIndex] = useState<number>(0)
  const [timeLeft, setTimeLeft] = useState<number | null>(null)

  const inputRef = useRef<HTMLInputElement>(null)

  // Initialize countdown when modal opens
  useEffect(() => {
    if (isOpen && ttlSeconds) {
      setTimeLeft(ttlSeconds)
    }
  }, [isOpen, ttlSeconds])

  // Countdown timer
  useEffect(() => {
    if (timeLeft !== null && timeLeft > 0 && isOpen) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000)
      return () => clearTimeout(timer)
    } else if (timeLeft === 0 && isOpen) {
      // Only show expiry message if countdown actually reached 0 (not initial state)
      toast.error('OTP has expired. Please request a new one.')
      dispatch(closeOtpModal())
    }
  }, [timeLeft, isOpen, dispatch])

  // Reset OTP when modal closes
  useEffect(() => {
    if (!isOpen) {
      setOtp(new Array(6).fill(''))
      setActiveOtpIndex(0)
      setTimeLeft(null)
    }
  }, [isOpen])

  const handleOtpChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = target

    let newOTP: string[] = [...otp]
    if (value.length > 6) {
      toast.error('OTP length should not be greater than 6!')
      return
    }
    if (value.length > 1 && value.length <= 6) {
      newOTP = value.split('')
      let newArr = new Array(6 - value.length).fill('')
      newOTP = newOTP.concat(newArr)
      setActiveOtpIndex(value.length)
    } else {
      newOTP[currentWithdrawOTPIndex] = value.substring(value.length - 1)
      if (!value) {
        newOTP[currentWithdrawOTPIndex] = ''
        setActiveOtpIndex(currentWithdrawOTPIndex - 1)
      } else {
        setActiveOtpIndex(currentWithdrawOTPIndex + 1)
      }
    }

    setOtp(newOTP)
  }

  const handleOnKeyDown = (
    { key }: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    currentWithdrawOTPIndex = index

    if (key === 'Backspace') {
      setActiveOtpIndex(currentWithdrawOTPIndex - 1)
    }
  }

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus()
    }
  }, [activeOtpIndex, isOpen])

  // Focus first input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    }
  }, [isOpen])

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault()

    const otpCode = otp.join('')
    if (otpCode.length !== 6) {
      toast.error('Please enter a complete 6-digit OTP code')
      return
    }

    if (!challengeId) {
      toast.error('Invalid OTP session. Please try again.')
      return
    }

    try {
      dispatch(setOtpLoading(true))
      await onOtpSubmit(challengeId, otpCode)
      dispatch(closeOtpModal())
      setOtp(new Array(6).fill(''))
    } catch (error: any) {
      console.log('OTP verification error:', error)
      toast.error(error?.message || 'OTP verification failed')
    } finally {
      dispatch(setOtpLoading(false))
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      dispatch(closeOtpModal())
    }
  }

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={handleClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative font-dashboard w-full max-w-md transform overflow-hidden rounded-2xl bg-[#161819] px-6 pt-6 pb-8 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title className="text-lg font-medium text-white">
                    Verify OTP
                  </Dialog.Title>
                  {!isLoading && (
                    <button
                      onClick={handleClose}
                      className="text-white/60 hover:text-white transition-colors"
                    >
                      <IoMdClose size={24} />
                    </button>
                  )}
                </div>

                <div className="mb-6">
                  <p className="text-sm text-white/70 mb-4">
                    We've sent a 6-digit verification code to your email. Please enter it below to complete your withdrawal.
                  </p>
                  
                  {timeLeft !== null && timeLeft > 0 && (
                    <p className="text-sm text-primary mb-4">
                      Code expires in: {formatTime(timeLeft)}
                    </p>
                  )}
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="flex justify-center gap-2 mb-6">
                    {otp.map((_, index) => (
                      <input
                        key={index}
                        ref={index === activeOtpIndex ? inputRef : null}
                        type="text"
                        className="w-12 h-12 text-center text-lg font-medium bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-primary focus:outline-none transition-colors"
                        value={otp[index]}
                        onChange={handleOtpChange}
                        onKeyDown={(e) => handleOnKeyDown(e, index)}
                        maxLength={6}
                        disabled={isLoading}
                      />
                    ))}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || otp.join('').length !== 6}
                    className="w-full py-3 px-4 bg-primary hover:bg-primary/90 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
                  >
                    {isLoading ? 'Verifying...' : 'Verify OTP'}
                  </button>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
